import React, { useEffect, useState } from "react";
import { C } from "../theme";
import Button from "./Button";
import { getStorageEstimate, isStoragePersisted } from "../services/storagePersistence";

/**
 * Cache & sync status sheet (Phase 9b).
 *
 * Shows what is queued offline ("cached"), per-item status/size/age, the
 * active upload progress once back online, device storage health, and a manual
 * "Retry now" action. Opened by tapping the offline/sync banner.
 */

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatAge(ts) {
  if (!ts) return "";
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const TYPE_ICON = { video: "🎬", catch: "🐟", alert: "📍" };

export default function CacheStatusModal({
  open,
  onClose,
  items = [],
  activeUpload = null,
  syncing = false,
  isOffline = false,
  lastSyncAt = null,
  onRetry,
}) {
  const [storage, setStorage] = useState(null);
  const [persisted, setPersisted] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([getStorageEstimate(), isStoragePersisted()]).then(([est, p]) => {
      if (cancelled) return;
      setStorage(est);
      setPersisted(p);
    });
    return () => {
      cancelled = true;
    };
  }, [open, items.length, syncing]);

  if (!open) return null;

  const total = items.length;
  const failedCount = items.filter((i) => i.status === "failed").length;
  const storageLow = storage && storage.percentUsed >= 90;

  const overlayStyle = {
    position: "absolute",
    inset: 0,
    background: "rgba(4,8,15,0.72)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 12000,
  };
  const sheetStyle = {
    width: "100%",
    maxHeight: "92%",
    overflowY: "auto",
    background: C.cardBg,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTop: `1px solid ${C.border}`,
    boxShadow: "0 -10px 40px rgba(0,0,0,0.5)",
    padding: "18px 18px calc(20px + env(safe-area-inset-bottom, 0px))",
    color: C.white,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Cache and sync status"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <span aria-hidden style={{ fontSize: 18 }}>☁︎</span>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Cached & Sync Status</h2>
        </div>
        <p style={{ margin: "2px 0 14px", fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          {isOffline
            ? "You're offline. Catches and videos are saved on this device and will upload automatically once you're back at the ramp."
            : syncing
            ? "Uploading your cached catches and videos…"
            : total > 0
            ? "Items waiting to upload."
            : "Everything is synced. Nothing is waiting to upload."}
        </p>

        {/* Summary row */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: total > 0 ? C.gold : C.seafoam,
              border: `1px solid ${C.border}`,
              borderRadius: 999,
              padding: "4px 10px",
            }}
          >
            {total > 0 ? `${total} cached` : "All synced ✓"}
          </span>
          {failedCount > 0 && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: C.red,
                border: `1px solid ${C.red}55`,
                borderRadius: 999,
                padding: "4px 10px",
              }}
            >
              {failedCount} need attention
            </span>
          )}
          {lastSyncAt && (
            <span style={{ fontSize: 11, color: C.muted, padding: "4px 0" }}>
              Last synced {formatAge(lastSyncAt)}
            </span>
          )}
        </div>

        {/* Items */}
        {total === 0 ? (
          <div
            style={{
              border: `1px dashed ${C.border}`,
              borderRadius: 12,
              padding: 18,
              textAlign: "center",
              color: C.muted,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            No cached items.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {items.map((item) => {
              const isActive = activeUpload && activeUpload.key === item.key;
              const pct = isActive ? activeUpload.pct : null;
              const statusLabel =
                item.status === "failed"
                  ? `Failed — ${item.attempts} attempts`
                  : isActive
                  ? `Uploading ${pct}%`
                  : item.attempts > 0
                  ? `Retrying (${item.attempts})`
                  : "Queued";
              const statusColor =
                item.status === "failed" ? C.red : isActive ? C.seafoam : C.muted;
              return (
                <div
                  key={item.key}
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: "10px 12px",
                    background: C.midnight || C.deepWater,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span aria-hidden style={{ fontSize: 16 }}>
                      {TYPE_ICON[item.type] || "📦"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.white,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                        {item.sizeBytes ? `${formatBytes(item.sizeBytes)} · ` : ""}
                        {formatAge(item.queuedAt)}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: statusColor, whiteSpace: "nowrap" }}>
                      {statusLabel}
                    </span>
                  </div>
                  {isActive && (
                    <div
                      style={{
                        height: 4,
                        borderRadius: 4,
                        background: `${C.border}`,
                        marginTop: 8,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${pct || 0}%`,
                          height: "100%",
                          background: C.seafoam,
                          transition: "width 0.2s ease",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Storage health */}
        {storage && (
          <div
            style={{
              border: `1px solid ${storageLow ? `${C.red}55` : C.border}`,
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: C.muted }}>Device storage</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: storageLow ? C.red : C.muted }}>
                {formatBytes(storage.usage)} / {formatBytes(storage.quota)}
              </span>
            </div>
            <div style={{ height: 4, borderRadius: 4, background: C.border, overflow: "hidden" }}>
              <div
                style={{
                  width: `${Math.round(storage.percentUsed)}%`,
                  height: "100%",
                  background: storageLow ? C.red : C.seafoam,
                }}
              />
            </div>
            {storageLow && (
              <div style={{ fontSize: 10, color: C.red, marginTop: 6 }}>
                Storage is nearly full — free up space to keep caching catches.
              </div>
            )}
            {persisted === false && (
              <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>
                Note: durable storage isn't granted on this device; cached data could be evicted under pressure.
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          {total > 0 && (
            <Button
              variant="primary"
              onClick={onRetry}
              disabled={isOffline || syncing}
              style={{ flex: 1 }}
            >
              {syncing ? "Syncing…" : "Retry now"}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={onClose}
            style={{ flex: total > 0 ? "0 0 auto" : 1 }}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
